// Audit log types and labels (ADR-0009, docs/design/denetim-logu.md, glossary §8). Shared by api/ and web/.
// Rule: `detail` never carries a password, a raw Excel cell value, header names, record keys or note text.
import type { Role } from "./api";
import { ROLE_LABEL } from "./permissions";
import { sourceTypeText } from "./sources";
import { findingStatusLabel } from "./findings";
import { formatNumber } from "./versions";

export const AUDIT_EVENT_TYPES = [
  "login_succeeded", "login_failed", "logout", "account_locked", "password_set", "password_changed",
  "upload_created", "upload_rejected", "manual_entry_created", "profile_saved", "run_completed", "finding_status_changed", "note_added",
  "report_generated", "file_downloaded", "request_forbidden",
  "user_created", "user_role_changed", "user_deactivated", "user_activated", "user_password_reset",
  "settings_applied", "pilot_purged",
] as const;
export type AuditEventType = (typeof AUDIT_EVENT_TYPES)[number];

export const AUDIT_OUTCOMES = ["success", "denied"] as const;
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

export const AUDIT_TARGET_TYPES = ["upload", "finding", "run", "report", "profile", "user", "settings_version"] as const;
export type AuditTargetType = (typeof AUDIT_TARGET_TYPES)[number];

/** Reason codes of a denied user command (glossary §8). */
export const USER_DENY_REASONS = [
  "ACTOR_ROLE_INSUFFICIENT", "TARGET_ROLE_NOT_MANAGEABLE", "ROLE_NOT_GRANTABLE", "LAST_SUPER_ADMIN", "SUPER_ADMIN_ONLY", "SELF_TARGET",
] as const;
export type UserDenyReason = (typeof USER_DENY_REASONS)[number];

export type AuditFilter = { user?: string; eventType?: AuditEventType; from?: string; to?: string };

type Empty = Record<string, never>;
type Denied = { reason: UserDenyReason };

export type AuditDetail = {
  login_succeeded: Empty;
  login_failed: Empty;
  logout: Empty;
  account_locked: { lockedUntil: string };
  password_set: Empty;
  password_changed: Empty;
  upload_created: { fileName: string; fileHash: string; sourceType: string; rowCount: number; justification?: string };
  manual_entry_created: { sourceType: string; operation: "create" | "correct"; basedOnUploadId: number | null; entryHash: string; justification: string };
  upload_rejected: { fileName: string; fileHash: string; sourceType: string; code: string; totalErrors?: number };
  profile_saved: { name: string; sourceType: string };
  run_completed: { durationMs: number; openBySeverity: { critical: number; warning: number; info: number } };
  finding_status_changed: { rule: string; from: string; to: string; runId?: number; uploadId?: number };
  note_added: { noteId: number };
  report_generated: Empty;
  file_downloaded: { kind: "pdf" | "xlsx"; screen: string; filter?: AuditFilter };
  request_forbidden: { method: string; path: string; permission: string };
  user_created: { role: Role } | ({ role: Role } & Denied);
  user_role_changed: { from: Role; to: Role } | ({ to: Role } & Denied);
  user_deactivated: Empty | Denied;
  user_activated: Empty | Denied;
  user_password_reset: Empty | Denied;
  settings_applied: { changes: { key: string; old: unknown; new: unknown }[]; justification: string } | ({ justification: string } & Denied);
  pilot_purged: { companyName: string; fileCount: number; recordCount: number } | Denied;
};

export type AuditEventInput<T extends AuditEventType = AuditEventType> = {
  [K in T]: {
    eventType: K;
    actorId: number | null;
    outcome: AuditOutcome;
    target?: { type: AuditTargetType; id: number };
    detail: AuditDetail[K];
  };
}[T];

/** One row of GET /api/audit-log (ADR-0005). `actor`: username, "setup" or null. */
export type AuditItem = {
  id: number;
  createdAt: string;
  actor: string | null;
  eventType: AuditEventType;
  outcome: AuditOutcome;
  target: { type: AuditTargetType; id: number } | null;
  detail: Record<string, unknown>;
};
export type AuditLogResponse = { items: AuditItem[]; total: number; page: number; pageSize: number };
/** Extra fields of 422 EXPORT_TOO_LARGE. */
export type ExportTooLargeExtra = { limit: number; total: number };

export const auditEventLabel: Record<AuditEventType, string> = {
  login_succeeded: "Giriş", login_failed: "Başarısız giriş", logout: "Çıkış", account_locked: "Hesap kilitlendi",
  password_set: "Parola belirlendi", password_changed: "Parola değiştirildi", upload_created: "Yükleme", manual_entry_created: "Elle giriş", upload_rejected: "Reddedilen yükleme",
  profile_saved: "Profil kaydı", run_completed: "Mutabakat koşusu", finding_status_changed: "Bulgu durumu değişti",
  note_added: "Not", report_generated: "Rapor üretildi", file_downloaded: "Dosya indirildi",
  request_forbidden: "Yetkisiz istek", user_created: "Kullanıcı eklendi", user_role_changed: "Kullanıcı rolü değişti",
  user_deactivated: "Kullanıcı pasifleştirildi", user_activated: "Kullanıcı etkinleştirildi",
  user_password_reset: "Parola sıfırlandı", settings_applied: "Ayar uygulandı", pilot_purged: "Pilot verisi silindi",
};
export const auditOutcomeLabel: Record<AuditOutcome, string> = { success: "Başarılı", denied: "Reddedildi" };
const targetLabel: Record<AuditTargetType, string> = {
  upload: "Yükleme", finding: "Bulgu", run: "Koşu", report: "Rapor", profile: "Profil", user: "Kullanıcı", settings_version: "Ayar sürümü",
};
const reasonLabel: Record<UserDenyReason, string> = {
  ACTOR_ROLE_INSUFFICIENT: "rol yetmiyor", TARGET_ROLE_NOT_MANAGEABLE: "hedef kullanıcının rolü yönetilemez",
  ROLE_NOT_GRANTABLE: "bu rol verilemez", LAST_SUPER_ADMIN: "son aktif Super Admin", SUPER_ADMIN_ONLY: "yalnız Super Admin",
  SELF_TARGET: "kullanıcı kendini hedefleyemez",
};

/** `screen` of file_downloaded → screen title (web/screens.ts). Old rows hold "E5"/"E8", new ones "e5"/"e8"/"e4": lookup ignores case. */
const SCREEN_NAME: Record<string, string> = {
  e1: "Durum Panosu", e2: "Veri Kaynakları", e3: "Mutabakat", e4: "İzleme Zinciri", e5: "DİİB Paneli", e6: "Veri Geçmişi", e7: "Denetim Raporu", e8: "Denetim Logu",
};
const permissionLabel: Record<string, string> = { view: "görüntüleme", write: "yazma", report: "rapor", audit: "denetim" };
const kindLabel: Record<string, string> = { pdf: "PDF", xlsx: "Excel" };
/** Reason of a rejected upload (ErrorCode of the 4xx/422 that rejected it). */
const rejectLabel: Record<string, string> = {
  ROW_ERRORS: "satır hatası", FILE_ALREADY_UPLOADED: "dosya zaten yüklenmiş", FILE_TOO_LARGE: "dosya çok büyük",
  REQUIRED_FIELDS_MISSING: "zorunlu alan eşlenmemiş", AMBIGUOUS_FORMAT: "biçim belirsiz", VALUE_MAP_MISSING: "değer eşlemesi eksik", JUSTIFICATION_REQUIRED: "gerekçe gerekli",
};
const label = <T extends string>(map: Record<string, string>, v: T) => map[v] ?? v;
const statusText = (v: unknown) => label(findingStatusLabel, String(v));
const roleText = (v: unknown) => label(ROLE_LABEL, String(v));

/** Plain text, e.g. "Yükleme #12". No link: after a pilot purge the target row is gone (ADR-0009). */
export const auditTargetText = (t: { type: AuditTargetType; id: number } | null) => (t ? `${targetLabel[t.type]} #${t.id}` : "—");
export const auditActorText = (a: string | null) => (a === "setup" ? "kurulum" : (a ?? "—"));

/** Europe/Istanbul, `GG.AA.YYYY SS:DD:ss`. */
export function formatAuditTime(iso: string | Date): string {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Istanbul", hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}:${p.second}`;
}

/** Short Turkish detail text for E8 and the xlsx. Reads only the fields documented per event. */
export function describeAuditEvent(eventType: AuditEventType, d: Record<string, unknown>): string {
  const parts: string[] = [];
  const add = (label: string, v: unknown) => { if (v !== undefined && v !== null && v !== "") parts.push(`${label}: ${typeof v === "number" ? formatNumber(v) : String(v)}`); };
  switch (eventType) {
    case "account_locked": add("Kilit bitişi", d.lockedUntil && formatAuditTime(String(d.lockedUntil))); break;
    case "upload_created": add("Dosya", d.fileName); add("Parmak izi", d.fileHash); add("Kaynak", d.sourceType && sourceTypeText(String(d.sourceType))); add("Satır", d.rowCount); add("Gerekçe", d.justification); break;
    case "manual_entry_created": add("Kaynak", d.sourceType && sourceTypeText(String(d.sourceType))); add("İşlem", d.operation === "create" ? "yeni kayıt" : "düzeltme"); add("Gerekçe", d.justification); break;
    case "upload_rejected": add("Dosya", d.fileName); add("Parmak izi", d.fileHash); add("Kaynak", d.sourceType && sourceTypeText(String(d.sourceType))); add("Neden", d.code && label(rejectLabel, String(d.code))); add("Hatalı satır", d.totalErrors); break;
    case "profile_saved": add("Profil", d.name); add("Kaynak", d.sourceType && sourceTypeText(String(d.sourceType))); break;
    case "run_completed": {
      const s = (d.openBySeverity ?? {}) as Record<string, number>;
      add("Süre", typeof d.durationMs === "number" ? `${(d.durationMs / 1000).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} sn` : undefined); add("Açık", `${formatNumber(s.critical ?? 0)} kritik, ${formatNumber(s.warning ?? 0)} uyarı, ${formatNumber(s.info ?? 0)} bilgi`); break;
    }
    case "finding_status_changed": add("Kural", d.rule); add("Durum", `${statusText(d.from)} → ${statusText(d.to)}`); break;
    case "note_added": add("Not no", d.noteId === undefined ? undefined : String(d.noteId)); break;
    case "file_downloaded": add("Tür", d.kind && label(kindLabel, String(d.kind))); add("Ekran", d.screen && label(SCREEN_NAME, String(d.screen).toLowerCase())); break;
    case "request_forbidden": add("İstek", `${d.method} ${d.path}`); add("İzin", d.permission && label(permissionLabel, String(d.permission))); break;
    case "user_created": add("Rol", d.role && roleText(d.role)); break;
    case "user_role_changed": add("Rol", d.from ? `${roleText(d.from)} → ${roleText(d.to)}` : `→ ${roleText(d.to)}`); break;
    case "settings_applied": add("Değişen ayar", Array.isArray(d.changes) ? d.changes.length : 0); add("Gerekçe", d.justification); break;
    case "pilot_purged": add("Firma", d.companyName); add("Dosya", d.fileCount); add("Kayıt", d.recordCount); break;
    default: break;
  }
  if (typeof d.reason === "string") add("Neden", reasonLabel[d.reason as UserDenyReason] ?? d.reason);
  return parts.join("; ");
}
